import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, Users, Calendar, ChevronRight, ChevronLeft, Sparkles, Check } from 'lucide-react';
import { Button } from './ui/button';

const ONBOARDING_KEY = 'trainers_onboarding_complete';

const steps = [
  {
    id: 'welcome',
    title: 'Benvenuto in Trainers! 👋',
    description: 'Ti guideremo attraverso i primi passi per configurare la tua attività di allenatore.',
    icon: Sparkles,
    color: 'text-[#ccff00]',
    bgColor: 'bg-[#ccff00]/10',
  },
  {
    id: 'venue',
    title: 'Aggiungi una Struttura',
    description: 'Inizia inserendo la struttura dove svolgi le lezioni. Potrai impostare il costo orario dell\'affitto che verrà diviso tra gli atleti.',
    icon: MapPin,
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/10',
    action: 'showVenueModal',
    actionLabel: 'Aggiungi Struttura',
  },
  {
    id: 'athlete',
    title: 'Aggiungi un Atleta',
    description: 'Ora aggiungi il tuo primo atleta. Potrai invitarlo via link per permettergli di registrarsi e visualizzare le sue lezioni.',
    icon: Users,
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10',
    action: 'showAthleteModal',
    actionLabel: 'Aggiungi Atleta',
  },
  {
    id: 'lesson',
    title: 'Crea la Prima Lezione',
    description: 'Perfetto! Ora puoi creare la tua prima lezione, selezionando: la struttura, gli atleti ed impostando i costi.',
    icon: Calendar,
    color: 'text-[#ccff00]',
    bgColor: 'bg-[#ccff00]/10',
    action: 'showLessonModal',
    actionLabel: 'Crea Lezione',
  },
  {
    id: 'complete',
    title: 'Tutto Pronto! 🎉',
    description: 'Hai completato la configurazione iniziale. Ora puoi gestire le tue lezioni, visualizzare i report e molto altro!',
    icon: Check,
    color: 'text-green-400',
    bgColor: 'bg-green-500/10',
  },
];

export const OnboardingOverlay = ({ 
  onClose, 
  onAction,
  currentStepOverride = null,
  completedSteps = []
}) => {
  const [currentStep, setCurrentStep] = useState(currentStepOverride || 0);
  
  // Sincronizza con lo step override quando cambia
  useEffect(() => {
    if (currentStepOverride !== null) {
      setCurrentStep(currentStepOverride);
    }
  }, [currentStepOverride]);
  
  const step = steps[currentStep];
  const Icon = step.icon;
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  const handleNext = () => {
    if (isLastStep) {
      localStorage.setItem(ONBOARDING_KEY, 'true');
      onClose();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleAction = () => {
    if (step.action && onAction) {
      // Passa l'azione e lo step corrente
      onAction(step.action, currentStep);
    }
  };

  const handleSkip = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-zinc-900 border border-white/10 rounded-2xl max-w-md w-full overflow-hidden shadow-2xl">
        {/* Progress indicators */}
        <div className="flex gap-1 p-4 pb-0">
          {steps.map((_, idx) => (
            <div 
              key={idx}
              className={`h-1 flex-1 rounded-full transition-colors ${
                idx <= currentStep ? 'bg-[#ccff00]' : 'bg-zinc-700'
              }`}
            />
          ))}
        </div>

        {/* Content */}
        <div className="p-6 text-center">
          <div className={`w-20 h-20 ${step.bgColor} rounded-full flex items-center justify-center mx-auto mb-6`}>
            <Icon className={`w-10 h-10 ${step.color}`} />
          </div>
          
          <h2 className="text-2xl font-bold text-white mb-3">{step.title}</h2>
          <p className="text-zinc-400 mb-6 leading-relaxed">{step.description}</p>

          {/* Action button for steps that have one */}
          {step.action && (
            <Button
              onClick={handleAction}
              className={`mb-4 w-full ${step.bgColor} ${step.color} hover:opacity-90 font-semibold rounded-xl h-12`}
            >
              {step.actionLabel}
            </Button>
          )}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between p-4 border-t border-white/10">
          <Button
            variant="ghost"
            onClick={handleSkip}
            className="text-zinc-500 hover:text-white"
          >
            Salta
          </Button>

          <div className="flex gap-2">
            {!isFirstStep && (
              <Button
                variant="outline"
                onClick={handlePrev}
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-xl"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Indietro
              </Button>
            )}
            <Button
              onClick={handleNext}
              className="bg-[#ccff00] text-black hover:bg-[#b3e600] font-semibold rounded-xl"
            >
              {isLastStep ? 'Inizia!' : 'Avanti'}
              {!isLastStep && <ChevronRight className="w-4 h-4 ml-1" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const useOnboarding = () => {
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);

  useEffect(() => {
    const completed = localStorage.getItem(ONBOARDING_KEY);
    if (!completed) {
      setIsNewUser(true);
    }
  }, []);

  const startOnboarding = useCallback(() => setShowOnboarding(true), []);
  const closeOnboarding = useCallback(() => setShowOnboarding(false), []);

  const resetOnboarding = useCallback(() => {
    localStorage.removeItem(ONBOARDING_KEY);
    setIsNewUser(true);
  }, []);

  return {
    showOnboarding,
    isNewUser,
    startOnboarding,
    closeOnboarding,
    resetOnboarding,
  };
};
